import { Add, Close, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { DialogContent, DialogTitle, IconButton } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { UserAvatar } from '@/tracker/entities/user';
import { formatIsoDuration } from '@/tracker/entities/worklog';
import { Timelog } from '@/tracker/types';
import { Loader } from '@/tracker/ui/Loader';
import { LoadMoreTrigger } from '@/tracker/ui/LoadMoreTrigger';
import { PresenceDialog } from '@/tracker/ui/PresenceDialog';

import { WorklogFormDialog } from '../WorklogFormDialog';
import { IssueCardStore } from '../../model/IssueCardStore';

import { formatDay, groupByDay } from './groupByDay';

import s from './WorklogHistoryDialog.module.scss';

type Props = {
  store: IssueCardStore;
  currentUserId: string | null;
  isAdmin: boolean;
  onAdd: () => void;
  onClose: () => void;
};

/** «История записей по учёту времени», как в Яндекс Трекере: записи по дням, свежие сверху, постранично */
const WorklogHistoryDialog: React.FC<Props> = observer(
  ({ store, currentUserId, isAdmin, onAdd, onClose }) => {
    // Удаление в два клика: запись нельзя восстановить
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [editing, setEditing] = useState<Timelog | null>(null);

    useEffect(() => {
      store.loadWorklogs();
    }, [store]);

    const isEmpty = store.isWorklogsLoaded && store.worklogs.length === 0;
    const isFirstPageLoading = store.isWorklogsLoading && store.worklogs.length === 0;

    return (
      <PresenceDialog onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle className={s.dialogTitle}>
          История записей по учёту времени
          <span className={s.dialogTitle__actions}>
            {!store.isReadOnly && (
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={onAdd}>
                Записать время
              </Button>
            )}
            <IconButton aria-label="Закрыть" onClick={onClose}>
              <Close />
            </IconButton>
          </span>
        </DialogTitle>
        <DialogContent dividers className={s.history}>
          {isFirstPageLoading && <Loader />}
          {isEmpty && <p className={s.empty}>Времени ещё не записано</p>}

          {groupByDay(store.worklogs).map(({ day, worklogs }) => (
            <section key={day}>
              <h4 className={s.day}>{formatDay(day)}</h4>
              <ul className={s.entries}>
                {worklogs.map((worklog) => (
                  <li key={worklog.id} className={s.entry}>
                    <UserAvatar name={worklog.user.displayName} size={32} />
                    <div>
                      <div className={s.entry__header}>
                        <span className={s.entry__author}>{worklog.user.displayName}</span>
                        <time dateTime={worklog.start}>
                          {DateTime.fromISO(worklog.start).toFormat('HH:mm')}
                        </time>
                      </div>
                      {worklog.comment && <div className={s.entry__comment}>{worklog.comment}</div>}
                    </div>
                    <span className={s.entry__duration}>{formatIsoDuration(worklog.duration)}</span>
                    {!store.isReadOnly && (isAdmin || worklog.user.id === currentUserId) && (
                      <span className={s.entry__actions}>
                        {pendingDeleteId === worklog.id ? (
                          <Button
                            variant="text"
                            color="error"
                            size="small"
                            disabled={store.isSaving}
                            onClick={() => store.deleteWorklog(worklog.id)}
                            onBlur={() => setPendingDeleteId(null)}
                            autoFocus
                          >
                            Удалить
                          </Button>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              className={s.entry__action}
                              aria-label="Изменить запись времени"
                              onClick={() => setEditing(worklog)}
                            >
                              <EditOutlined fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              className={s.entry__action}
                              aria-label="Удалить запись времени"
                              onClick={() => setPendingDeleteId(worklog.id)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <LoadMoreTrigger
            hasMore={store.hasMoreWorklogs}
            isLoading={store.isWorklogsLoading}
            isError={store.isWorklogsError}
            onLoadMore={store.loadMoreWorklogs}
          />
        </DialogContent>

        <AnimatePresence>
          {editing && (
            <WorklogFormDialog
              open
              title={`Изменить запись · ${editing.user.displayName}`}
              estimation={null}
              worklog={editing}
              disabled={store.isSaving}
              onSubmit={async ({ worklog }) => {
                if (await store.updateWorklog({ id: editing.id, data: worklog })) setEditing(null);
              }}
              onCancel={() => setEditing(null)}
            />
          )}
        </AnimatePresence>
      </PresenceDialog>
    );
  },
);

export { WorklogHistoryDialog };
